import { API } from '@shared/types/api';
import { MatchComponent } from './indexedValues';

/** The parts of a workflow template that matching reads. */
export interface MatchWorkflowTemplate {
  id: string;
  components?: MatchComponent[] | null;
  archivedComponents?: MatchComponent[] | null;
}

interface MatchRefs {
  workflowTemplateRefs?: MatchWorkflowTemplate[] | null;
  workflowEntityRefs?: API.WorkflowEntity[] | null;
}

/** What matching a card needs beyond the card itself. */
export interface CardMatchLookups {
  getWorkflowTemplate(workflowTemplateId: string): MatchWorkflowTemplate | undefined;
  /** A component of a workflow template, archived ones included - a filter can still name one. */
  getComponent(workflowTemplateId: string, componentId: string): MatchComponent | undefined;
  /** A related card, as the subscription event carries it. */
  getRefCard(cardId: string): API.WorkflowEntity | undefined;
  currentUserId?: number;
  now: Date;
}

interface GetCardMatchLookupsProps {
  eventRefs: MatchRefs | null | undefined;
  listRefs: MatchRefs | null | undefined;
  currentUserId?: number;
  now?: Date;
}

/**
 * Templates come from the event's refs and then the list's. Related cards come from the event's
 * alone: the list's copies were loaded with it and may predate an edit to them.
 */
export function getCardMatchLookups({
  eventRefs,
  listRefs,
  currentUserId,
  now = new Date(),
}: GetCardMatchLookupsProps): CardMatchLookups {
  const workflowTemplates = [
    ...(eventRefs?.workflowTemplateRefs ?? []),
    ...(listRefs?.workflowTemplateRefs ?? []),
  ];
  const getWorkflowTemplate = (workflowTemplateId: string) =>
    workflowTemplates.find(workflowTemplate => workflowTemplate.id === workflowTemplateId);

  return {
    getWorkflowTemplate,
    getComponent: (workflowTemplateId, componentId) => {
      const workflowTemplate = getWorkflowTemplate(workflowTemplateId);
      return [
        ...(workflowTemplate?.components ?? []),
        ...(workflowTemplate?.archivedComponents ?? []),
      ].find(component => component.id === componentId);
    },
    getRefCard: cardId => eventRefs?.workflowEntityRefs?.find(card => card.id === cardId),
    currentUserId,
    now,
  };
}
